import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import PublicBrowseShell from '../../components/public/PublicBrowseShell';
import Select from '../../components/ui/Select';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import { leagueService } from '../../services/leagueService';
import { tournamentService } from '../../services/tournamentService';
import { playerService } from '../../services/playerService';
import { filterPublicCompetitions } from '../../utils/publicCompetitionVisibility';
import PlayerStatsCard from '../player-statistics/components/PlayerStatsCard';
import LeaderboardSection from '../player-statistics/components/LeaderboardSection';
import ComparisonPanel from '../player-statistics/components/ComparisonPanel';

const SCOPE_OPTIONS = [
  { value: 'league', label: 'League Players' },
  { value: 'tournament', label: 'Tournament Players' },
];

const SORT_OPTIONS = [
  { value: 'points', label: 'Points Per Game' },
  { value: 'rebounds', label: 'Rebounds Per Game' },
  { value: 'assists', label: 'Assists Per Game' },
  { value: 'efficiency', label: 'Efficiency Rating' },
  { value: 'name', label: 'Player Name' },
];

const FALLBACK_PHOTO = '/assets/images/no_image.png';

const formatPosition = (position) => {
  if (!position || position === '—') return 'N/A';
  return String(position)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

const computeEfficiency = ({ ppg = 0, rpg = 0, apg = 0, steals = 0, blocks = 0, fgPct = 0 }) => {
  const value = ppg + rpg * 0.8 + apg * 1.1 + steals * 1.2 + blocks * 1.2 + fgPct * 0.1;
  return Number(value.toFixed(1));
};

const PublicStatsPage = () => {
  const navigate = useNavigate();
  const [scope, setScope] = useState('league');
  const [leagueId, setLeagueId] = useState('all');
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [sortBy, setSortBy] = useState('points');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [leagues, setLeagues] = useState([]);
  const [players, setPlayers] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageAlt, setSelectedImageAlt] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const playersPerPage = 12;

  const leagueOptions = useMemo(
    () => [
      { value: 'all', label: 'All Leagues' },
      ...leagues.map((league) => ({
        value: league.id,
        label: `${league.name}${league.season ? ` · ${league.season}` : ''}`,
      })),
    ],
    [leagues]
  );

  const teamOptions = useMemo(() => {
    const unique = new Map();
    players.forEach((player) => {
      const id = player.teamId || player.team?.id;
      const name = player.team?.name || player.teamName;
      if (id && name && !unique.has(id)) unique.set(id, name);
    });
    return [
      { value: 'all', label: 'All Teams' },
      ...Array.from(unique.entries())
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([value, label]) => ({ value, label })),
    ];
  }, [players]);

  const loadStats = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (scope === 'tournament') {
        const data = await tournamentService.getRegionalPlayerStatistics();
        setPlayers(
          (data || []).map((row) => {
            const gp = row.gamesPlayed || 0;
            const ppg = gp ? Number((row.points / gp).toFixed(1)) : 0;
            const rpg = gp ? Number((row.rebounds / gp).toFixed(1)) : 0;
            const apg = gp ? Number((row.assists / gp).toFixed(1)) : 0;
            const stealsPg = gp ? Number((row.steals / gp).toFixed(1)) : 0;
            const blocksPg = gp ? Number((row.blocks / gp).toFixed(1)) : 0;
            const photo = row.photoUrl || FALLBACK_PHOTO;
            return {
              id: row.playerId,
              name: row.fullName || 'Player',
              photo,
              photoUrl: photo,
              photoAlt: `Photo of ${row.fullName || 'player'}`,
              jerseyNumber: row.jerseyNumber ?? '—',
              position: formatPosition(row.position),
              teamId: row.teamId || null,
              teamName: row.teamName || '—',
              team: { id: row.teamId, name: row.teamName || '—', logoUrl: row.teamLogoUrl },
              stats: {
                gamesPlayed: gp,
                pointsPerGame: ppg,
                reboundsPerGame: rpg,
                assistsPerGame: apg,
                stealsPerGame: stealsPg,
                blocksPerGame: blocksPg,
                fieldGoalPercentage: 0,
                efficiency: computeEfficiency({
                  ppg,
                  rpg,
                  apg,
                  steals: stealsPg,
                  blocks: blocksPg,
                }),
              },
              shootingStats: {
                threePointPercentage: 0,
                freeThrowPercentage: 0,
                threePointsMade: 0,
                freeThrowsMade: 0,
                fieldGoalsMade: 0,
                totalShots: 0,
              },
              advancedStats: {
                efficiency: computeEfficiency({
                  ppg,
                  rpg,
                  apg,
                  steals: stealsPg,
                  blocks: blocksPg,
                }),
                plusMinus: 0,
                usageRate: 0,
                trueShootingPercentage: 0,
                assistTurnoverRatio: 0,
                reboundRate: 0,
              },
              achievements: [],
              sourceType: 'tournament',
            };
          })
        );
      } else {
        let data = [];
        if (leagueId === 'all') {
          if (leagues.length > 0) {
            const batches = await Promise.all(
              leagues.map((league) =>
                playerService.getPlayersWithStatistics({ leagueId: league.id })
              )
            );
            const byId = new Map();
            batches.flat().forEach((player) => {
              if (!player?.id) return;
              const existing = byId.get(player.id);
              if (
                !existing ||
                (player.stats?.gamesPlayed || 0) > (existing.stats?.gamesPlayed || 0)
              ) {
                byId.set(player.id, player);
              }
            });
            data = Array.from(byId.values());
          }
        } else {
          data = await playerService.getPlayersWithStatistics({ leagueId });
        }

        setPlayers(
          (data || []).map((player) => {
            const photo =
              player.photoUrl ||
              player.photo ||
              playerService.getPlayerPhotoUrl?.(player) ||
              FALLBACK_PHOTO;
            return {
              ...player,
              name: player.name || 'Player',
              photo,
              photoUrl: photo,
              photoAlt: player.photoAlt || `Photo of ${player.name || 'player'}`,
              jerseyNumber: player.jerseyNumber ?? '—',
              position: formatPosition(player.playerPosition || player.position),
              teamId: player.team?.id || player.teamId || null,
              teamName: player.team?.name || '—',
              team: player.team || { id: null, name: '—' },
              stats: {
                gamesPlayed: player.stats?.gamesPlayed || 0,
                pointsPerGame: player.stats?.pointsPerGame || 0,
                reboundsPerGame: player.stats?.reboundsPerGame || 0,
                assistsPerGame: player.stats?.assistsPerGame || 0,
                stealsPerGame: player.stats?.stealsPerGame || 0,
                blocksPerGame: player.stats?.blocksPerGame || 0,
                fieldGoalPercentage: player.stats?.fieldGoalPercentage || 0,
                efficiency: player.stats?.efficiency || 0,
              },
              shootingStats: player.shootingStats || {},
              advancedStats: player.advancedStats || {
                efficiency: player.stats?.efficiency || 0,
              },
              achievements: player.achievements || [],
              sourceType: 'league',
            };
          })
        );
      }
    } catch (err) {
      console.error('Failed to load public stats:', err);
      setError(err.message || 'Failed to load player statistics.');
      setPlayers([]);
    } finally {
      setIsLoading(false);
    }
  }, [scope, leagueId, leagues]);

  useEffect(() => {
    document.title = 'Player Statistics - Tanzania Basketball Federation';
    leagueService
      .getAll()
      .then((data) => setLeagues(filterPublicCompetitions(data || [])))
      .catch(() => setLeagues([]));
  }, []);

  useEffect(() => {
    loadStats();
    setSelectedPlayers([]);
    setSelectedTeam('all');
    setCurrentPage(1);
  }, [loadStats]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedTeam, sortBy, scope]);

  const filteredPlayers = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = [...players];

    if (term) {
      list = list.filter(
        (player) =>
          player.name?.toLowerCase().includes(term) ||
          player.team?.name?.toLowerCase().includes(term) ||
          player.position?.toLowerCase().includes(term)
      );
    }

    if (selectedTeam !== 'all') {
      list = list.filter(
        (player) => player.teamId === selectedTeam || player.team?.id === selectedTeam
      );
    }

    list.sort((a, b) => {
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'rebounds') {
        return (b.stats?.reboundsPerGame || 0) - (a.stats?.reboundsPerGame || 0);
      }
      if (sortBy === 'assists') {
        return (b.stats?.assistsPerGame || 0) - (a.stats?.assistsPerGame || 0);
      }
      if (sortBy === 'efficiency') {
        return (b.stats?.efficiency || 0) - (a.stats?.efficiency || 0);
      }
      return (b.stats?.pointsPerGame || 0) - (a.stats?.pointsPerGame || 0);
    });

    return list;
  }, [players, search, selectedTeam, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredPlayers.length / playersPerPage));
  const currentPlayers = filteredPlayers.slice(
    (currentPage - 1) * playersPerPage,
    currentPage * playersPerPage
  );

  const playerHref = (player) =>
    scope === 'tournament' || player?.sourceType === 'tournament'
      ? `/regional-team-player-statistics?player=${encodeURIComponent(player.id)}`
      : `/browse-players/${player.id}`;

  const teamHref = (player) => {
    const teamId = player?.teamId || player?.team?.id;
    if (!teamId) return null;
    return scope === 'tournament' || player?.sourceType === 'tournament'
      ? `/regional-teams-profiles?team=${encodeURIComponent(teamId)}`
      : `/team-profiles?team=${encodeURIComponent(teamId)}&tab=statistics`;
  };

  const openPlayer = (player) => {
    if (!player?.id) return;
    navigate(playerHref(player));
  };

  const openTeam = (player) => {
    const href = teamHref(player);
    if (href) navigate(href);
  };

  const handlePlayerCompare = (player) => {
    setSelectedPlayers((prev) => {
      const exists = prev.some((p) => p.id === player.id);
      if (exists) return prev.filter((p) => p.id !== player.id);
      if (prev.length >= 4) return prev;
      return [...prev, player];
    });
  };

  const handleImageClick = (photo, photoAlt) => {
    setSelectedImage(photo || FALLBACK_PHOTO);
    setSelectedImageAlt(photoAlt || 'Player photo');
    setImageModalOpen(true);
  };

  const scopeLabel = scope === 'tournament' ? 'Tournament' : 'League';

  return (
    <>
      <Helmet>
        <title>Player Statistics - Tanzania Basketball Federation</title>
        <meta
          name="description"
          content="Browse league and tournament player statistics for Tanzania Basketball Federation."
        />
      </Helmet>

      <PublicBrowseShell>
        <div className="space-y-6">
          <div className="bg-card rounded-lg border border-border p-6 card-shadow">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-foreground mb-2">Player Statistics</h1>
                <p className="text-muted-foreground">
                  Comprehensive performance analytics and comparison tools for basketball players
                </p>
                <p className="text-sm text-primary mt-2 font-medium">
                  Showing {scopeLabel.toLowerCase()} players
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-6">
              <Select
                label="Competition Type"
                options={SCOPE_OPTIONS}
                value={scope}
                onChange={(value) => {
                  setScope(value);
                  setLeagueId('all');
                }}
              />
              {scope === 'league' && (
                <Select
                  label="League"
                  options={leagueOptions}
                  value={leagueId}
                  onChange={setLeagueId}
                />
              )}
              <Input
                type="search"
                label="Search"
                placeholder="Search players..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Select
                label="Team"
                options={teamOptions}
                value={selectedTeam}
                onChange={setSelectedTeam}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            <div className="xl:col-span-2 space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant={viewMode === 'grid' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewMode('grid')}
                      iconName="Grid3X3"
                    />
                    <Button
                      variant={viewMode === 'list' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewMode('list')}
                      iconName="List"
                    />
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {filteredPlayers.length} players found
                    <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs font-medium">
                      {scopeLabel}
                    </span>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <span className="text-sm text-muted-foreground">Sort by:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="text-sm border border-border rounded-md px-3 py-1 bg-background text-foreground"
                  >
                    {SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={loadStats}
                    className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                  >
                    <Icon name="RefreshCcw" size={14} />
                    Refresh
                  </button>
                </div>
              </div>

              {isLoading ? (
                <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
                  <Icon name="Loader2" size={48} className="text-primary mx-auto mb-4 animate-spin" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">Loading Statistics</h3>
                  <p className="text-muted-foreground">
                    Fetching {scopeLabel.toLowerCase()} player data...
                  </p>
                </div>
              ) : error ? (
                <div className="bg-card rounded-lg border border-border p-8 card-shadow text-center">
                  <Icon name="AlertTriangle" size={48} className="text-error mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">Error Loading Data</h3>
                  <p className="text-muted-foreground">{error}</p>
                </div>
              ) : currentPlayers.length > 0 ? (
                <div
                  className={
                    viewMode === 'grid'
                      ? 'grid grid-cols-1 lg:grid-cols-2 gap-6'
                      : 'space-y-4'
                  }
                >
                  {currentPlayers.map((player) => (
                    <PlayerStatsCard
                      key={player.id}
                      player={player}
                      onCompare={handlePlayerCompare}
                      onImageClick={handleImageClick}
                      onNameClick={openPlayer}
                      onTeamClick={openTeam}
                      isSelected={selectedPlayers.some((p) => p.id === player.id)}
                      showCompare
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-card rounded-lg border border-border p-8 card-shadow text-center">
                  <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">No Players Found</h3>
                  <p className="text-muted-foreground">
                    Try adjusting your search criteria or switch competition type
                  </p>
                </div>
              )}

              {totalPages > 1 && !isLoading && !error && (
                <div className="flex items-center justify-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPage === 1}
                    iconName="ChevronLeft"
                  />
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum =
                        Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                          className="w-10 h-10"
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={currentPage === totalPages}
                    iconName="ChevronRight"
                  />
                </div>
              )}

              {selectedPlayers.length > 0 && (
                <ComparisonPanel
                  selectedPlayers={selectedPlayers}
                  onRemovePlayer={(player) =>
                    setSelectedPlayers((prev) => prev.filter((p) => p.id !== player.id))
                  }
                  onClearAll={() => setSelectedPlayers([])}
                  onImageClick={handleImageClick}
                  onNameClick={openPlayer}
                />
              )}
            </div>

            <div className="xl:col-span-1">
              <LeaderboardSection
                players={filteredPlayers}
                title={`${scopeLabel} Leaders`}
                subtitle={`Top ${scopeLabel.toLowerCase()} performers in key statistical categories`}
                viewAllHref="/public-stats"
                onPlayerSelect={(player) => {
                  const full = players.find((p) => p.id === player.id);
                  if (full) handlePlayerCompare(full);
                }}
                onImageClick={handleImageClick}
                onNameClick={(player) => {
                  const full = players.find((p) => p.id === player.id) || player;
                  openPlayer(full);
                }}
              />
            </div>
          </div>
        </div>
      </PublicBrowseShell>

      {imageModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setImageModalOpen(false)}
          />
          <div className="relative bg-card border border-border rounded-lg p-4 max-w-md w-full card-shadow">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-foreground truncate">
                {selectedImageAlt}
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setImageModalOpen(false)}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>
            <div className="rounded-lg overflow-hidden bg-muted aspect-square">
              <Image
                src={selectedImage}
                alt={selectedImageAlt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PublicStatsPage;
